import { createContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  signup: (name: string, email: string, phone: string, password: string) => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CURRENT_USER_KEY = 'medtour_current_user';
const USERS_LIST_KEY = 'medtour_users_list';

interface StoredUser extends User {
  password: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  // 초기 로드 시 localStorage에서 현재 사용자 복원
  useEffect(() => {
    const storedUser = localStorage.getItem(CURRENT_USER_KEY);
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem(CURRENT_USER_KEY);
      }
    }
  }, []);

  // 사용자 목록 가져오기
  const getUsersList = (): StoredUser[] => {
    const stored = localStorage.getItem(USERS_LIST_KEY);
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  };

  // 사용자 목록 저장하기
  const saveUsersList = (users: StoredUser[]) => {
    localStorage.setItem(USERS_LIST_KEY, JSON.stringify(users));
  };

  // 회원가입
  const signup = async (
    name: string,
    email: string,
    phone: string,
    password: string
  ): Promise<boolean> => {
    const users = getUsersList();

    // 이메일 중복 체크
    if (users.some(u => u.email === email)) {
      return false;
    }

    // 새 사용자 생성
    const newUser: StoredUser = {
      id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name,
      email,
      phone,
      password,
      createdAt: new Date().toISOString(),
    };

    // 사용자 목록에 추가
    users.push(newUser);
    saveUsersList(users);

    // 자동 로그인
    const { password: _, ...userWithoutPassword } = newUser;
    setUser(userWithoutPassword);
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(userWithoutPassword));

    return true;
  };

  // 로그인
  const login = async (email: string, password: string): Promise<boolean> => {
    const users = getUsersList();
    const foundUser = users.find(u => u.email === email && u.password === password);

    if (!foundUser) {
      return false;
    }

    // 비밀번호 제외하고 저장
    const { password: _, ...userWithoutPassword } = foundUser;
    setUser(userWithoutPassword);
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(userWithoutPassword));

    return true;
  };

  // 로그아웃
  const logout = () => {
    setUser(null);
    localStorage.removeItem(CURRENT_USER_KEY);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    signup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}